import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    IconButton,
    Typography,
    styled,
    Fade,
    Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSwipeable } from 'react-swipeable';
import AppCard from './AppCard';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxWidth: 450,
        width: '90%',
        // margin/radius come from the MuiDialog theme override
        // (DIALOG_MARGIN / DIALOG_RADIUS in themes/theme.js)
        backgroundColor: 'white',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
    },
}));

const ModalHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ModalContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    position: 'relative'
}));

const FeatureCardWrapper = styled(Box)(({ theme }) => ({
    '& .MuiCard-root': {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
            transform: 'none',
            boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
        },
    },
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const NewFeaturesModal = ({
    open,
    onClose,
    features = []
}) => {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (features.length === 0) {
            setActiveIndex(0);
        }
    }, [features]);

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % features.length);
    };

    const handlePrev = () => {
        setActiveIndex((prev) => (prev - 1 + features.length) % features.length);
    };

    const handleCardClick = (card) => {
        card?.default_link && window.open(card?.default_link);
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    // useSwipeable hook for swipe gestures
    const handlers = useSwipeable({
        onSwipedLeft: handleNext,
        onSwipedRight: handlePrev,
        preventScrollOnSwipe: true
    });

    if (!features || features.length === 0) return null;

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
            transitionDuration={300}
            maxWidth="sm"
            fullWidth
        >
            {/* <ModalHeader> */}
            <Typography fontWeight={700} fontSize={"1.5rem"} sx={{ marginBlock: "2rem 1rem" }} textAlign={'center'}>
                {"Get Started"}
            </Typography>
            <CloseButton onClick={handleClose} size="small" sx={{ position: 'absolute', top: 10, right: 10 }}>
                <CloseIcon fontSize="small" />
            </CloseButton>
            {/* </ModalHeader> */}
            <ModalContent>
                <Box sx={{ position: 'relative' }}>
                    {features.length > 1 && (
                        <ArrowButton onClick={handlePrev} sx={{ left: { xs: -20, sm: -10 } }}>
                            <ChevronLeftIcon />
                        </ArrowButton>
                    )}
                    <FeatureCardWrapper {...handlers}>
                        <AppCard
                            card={features[activeIndex]}
                            onClick={() => handleCardClick(features[activeIndex])}
                            actionSectionStyle={{
                                paddingTop: "1rem"
                            }}
                            button_text={features[activeIndex]?.default_button_text}
                        />
                    </FeatureCardWrapper>
                    {features.length > 1 && (
                        <ArrowButton onClick={handleNext} sx={{ right: { xs: -20, sm: -10 } }}>
                            <ChevronRightIcon />
                        </ArrowButton>
                    )}
                </Box>
                {features.length > 1 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        {features.map((_, index) => (
                            <Box
                                key={index}
                                width={8}
                                height={8}
                                borderRadius="50%"
                                mx={0.5}
                                bgcolor={index === activeIndex ? 'primary.main' : 'grey.300'}
                            />
                        ))}
                    </Box>
                )}
            </ModalContent>
        </StyledDialog>
    );
};

export default NewFeaturesModal;
